import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { randomUUID } from 'node:crypto'

/**
 * CARE Program service: Client-Aligned Rate Enrollment.
 *
 * Therapist submits a request for one of their clients. Their supervisor
 * approves; on approval the reduced rate is applied to the originating
 * episode (and every member episode in the same group, for couples/group
 * therapy) plus all not-yet-attended sessions. A daily cron expires
 * requests on their `endDate` and reverts the rate.
 */

export type CareTier = 'RESIDENT' | 'ASSOCIATE'
export type CareRequestStatus =
  | 'SUPERVISOR_REVIEW'
  | 'REJECTED'
  | 'ACTIVE'
  | 'EXPIRED'
  | 'CANCELLED'

export interface CareProofDocument {
  path: string
  name: string
  type: string
  size: number
  uploadedAt: string
}

export interface CareRequest {
  id: string
  episodeId: string
  groupId: string | null
  therapistProId: string
  supervisorProId: string
  tier: CareTier
  status: CareRequestStatus
  careSessionRate: number
  originalSessionRate: number | null
  startDate: string
  endDate: string
  reason: string
  carePlan: string
  proofDocuments: CareProofDocument[]
  createdAt: string
  updatedAt: string
}

// Tier-specific rate bands (whole dollars per session, matches the
// units used in `episodes.sessionRate` and `sessions.rate`).
// Lifted from policy doc, enforced server-side.
export const CARE_RATE_BANDS: Record<CareTier, { min: number; max: number }> = {
  RESIDENT: { min: 15, max: 30 },
  ASSOCIATE: { min: 75, max: 100 },
}

// Tier-specific maximum duration in months.
export const CARE_MAX_DURATION_MONTHS: Record<CareTier, number> = {
  RESIDENT: 4,
  ASSOCIATE: 6,
}

export const CARE_MAX_ACTIVE_PER_THERAPIST = 3

export const CARE_HARDSHIP_PROOF_BUCKET = 'care-hardship-proofs'

function getAdminClient(): SupabaseClient {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const key =
    process.env.SUPABASE_SERVICE_KEY || process.env.SUPABASE_SERVICE_ROLE_KEY
  if (!url || !key) {
    throw new Error('Missing Supabase admin credentials for care service')
  }
  return createClient(url, key, {
    auth: { autoRefreshToken: false, persistSession: false },
  })
}

function tierFromEmploymentStatus(status: string | null | undefined): CareTier {
  return status === 'RESIDENT' ? 'RESIDENT' : 'ASSOCIATE'
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10)
}

export interface CreateCareRequestInput {
  episodeId: string
  therapistProId: string
  careSessionRate: number
  reason: string
  carePlan: string
  proofDocuments: CareProofDocument[]
  startDate?: string // defaults to today
}

export interface CareValidationError {
  code:
    | 'EPISODE_NOT_FOUND'
    | 'EPISODE_NOT_OWNED'
    | 'NO_SUPERVISOR'
    | 'RATE_OUT_OF_BAND'
    | 'CAP_REACHED'
    | 'DUPLICATE_REQUEST'
    | 'PROOF_REQUIRED'
  message: string
}

export class CareError extends Error {
  constructor(
    public code: CareValidationError['code'],
    message: string,
  ) {
    super(message)
    this.name = 'CareError'
  }
}

/**
 * Validates a CARE request, computes tier + endDate, and inserts the row at
 * SUPERVISOR_REVIEW. Caller must ensure the proof files have been uploaded
 * to the care-hardship-proofs bucket under care/{id}/* before calling.
 */
export async function createCareRequest(
  input: CreateCareRequestInput,
): Promise<CareRequest> {
  const supabase = getAdminClient()

  if (!input.proofDocuments || input.proofDocuments.length === 0) {
    throw new CareError(
      'PROOF_REQUIRED',
      'At least one proof-of-hardship document is required.',
    )
  }

  // Fetch episode (must belong to therapist).
  const { data: episode, error: episodeErr } = await supabase
    .from('episodes')
    .select('id, "therapistId", "groupId", "sessionRate"')
    .eq('id', input.episodeId)
    .maybeSingle()
  if (episodeErr || !episode) {
    throw new CareError('EPISODE_NOT_FOUND', 'Episode not found.')
  }
  if (episode.therapistId !== input.therapistProId) {
    throw new CareError(
      'EPISODE_NOT_OWNED',
      'This episode is not assigned to you.',
    )
  }

  // Look up therapist for supervisor + tier.
  const { data: therapist, error: therapistErr } = await supabase
    .from('pros')
    .select('id, "supervisorId", "employmentStatus"')
    .eq('id', input.therapistProId)
    .maybeSingle()
  if (therapistErr || !therapist) {
    throw new CareError('EPISODE_NOT_OWNED', 'Therapist record not found.')
  }

  // If the therapist has no supervisor on file, route the request to the
  // shared Alli admin inbox (the Pro row whose email is hello@alli.io).
  // That account is monitored by the admin team — no individual picks
  // anything up — and any admin can approve/reject via
  // care_supervisor_approve (the DB function accepts `is_admin()` as well
  // as the assigned supervisor).
  let assignedSupervisorProId: string | null = therapist.supervisorId
  if (!assignedSupervisorProId) {
    const inboxEmail =
      process.env.CARE_ADMIN_INBOX_EMAIL || 'hello@alli.io'
    const { data: inboxPro } = await supabase
      .from('pros')
      .select('id')
      .eq('email', inboxEmail)
      .maybeSingle()
    if (!inboxPro) {
      throw new CareError(
        'NO_SUPERVISOR',
        `No reviewer available — the ${inboxEmail} account is missing. Contact ops.`,
      )
    }
    assignedSupervisorProId = inboxPro.id
  }

  const tier = tierFromEmploymentStatus(therapist.employmentStatus)
  const band = CARE_RATE_BANDS[tier]
  if (input.careSessionRate < band.min || input.careSessionRate > band.max) {
    throw new CareError(
      'RATE_OUT_OF_BAND',
      `CARE rate for ${tier === 'RESIDENT' ? 'interns' : 'associates'} must be between $${band.min} and $${band.max}.`,
    )
  }

  // 3-active cap check.
  const { count: activeCount, error: countErr } = await supabase
    .from('care_requests')
    .select('id', { count: 'exact', head: true })
    .eq('therapistProId', input.therapistProId)
    .eq('status', 'ACTIVE')
  if (countErr) {
    throw new Error(`care.count_failed: ${countErr.message}`)
  }
  if ((activeCount ?? 0) >= CARE_MAX_ACTIVE_PER_THERAPIST) {
    throw new CareError(
      'CAP_REACHED',
      `You already have ${CARE_MAX_ACTIVE_PER_THERAPIST} active CARE clients — the maximum allowed.`,
    )
  }

  // Duplicate-open check (the DB also enforces this via unique partial idx).
  const { data: existingOpen } = await supabase
    .from('care_requests')
    .select('id')
    .eq('episodeId', input.episodeId)
    .in('status', ['SUPERVISOR_REVIEW', 'ACTIVE'])
    .maybeSingle()
  if (existingOpen) {
    throw new CareError(
      'DUPLICATE_REQUEST',
      'There is already an open CARE request for this client.',
    )
  }

  const start = input.startDate
    ? new Date(input.startDate)
    : new Date(new Date().toISOString().slice(0, 10))
  const end = addMonths(start, CARE_MAX_DURATION_MONTHS[tier])

  const now = new Date().toISOString()
  const id = randomUUID()

  const escalatedToAdmin = !therapist.supervisorId

  const row = {
    id,
    episodeId: input.episodeId,
    groupId: episode.groupId ?? null,
    therapistProId: input.therapistProId,
    supervisorProId: assignedSupervisorProId,
    tier,
    status: 'SUPERVISOR_REVIEW' as const,
    careSessionRate: input.careSessionRate,
    originalSessionRate: null,
    startDate: toDateString(start),
    endDate: toDateString(end),
    reason: input.reason.trim(),
    carePlan: input.carePlan.trim(),
    proofDocuments: input.proofDocuments,
    createdAt: now,
    updatedAt: now,
  }

  const { data, error } = await supabase
    .from('care_requests')
    .insert(row)
    .select('*')
    .single()
  if (error || !data) {
    throw new Error(`care.create_failed: ${error?.message ?? 'unknown'}`)
  }

  // Audit: submitted
  await supabase.from('audit_logs').insert({
    id: randomUUID(),
    proId: input.therapistProId,
    userRole: 'therapist',
    action: 'submitted',
    resource: 'care_request',
    resourceId: id,
    details: {
      tier,
      careSessionRate: input.careSessionRate,
      episodeId: input.episodeId,
      groupId: episode.groupId,
      supervisorProId: assignedSupervisorProId,
      escalatedToAdmin,
    },
    createdAt: now,
  })

  return data as CareRequest
}

/**
 * Supervisor approve. Delegates the atomic rate-flip to the Postgres
 * `care_supervisor_approve` SECURITY DEFINER function. The auth context for
 * the RPC is the supervisor's session, not service role, so the helper
 * picks up `current_pro_id()` correctly.
 */
export async function supervisorApprove(
  client: SupabaseClient,
  requestId: string,
): Promise<CareRequest> {
  const { data, error } = await client.rpc('care_supervisor_approve', {
    p_request_id: requestId,
  })
  if (error) throw new Error(`care.approve_failed: ${error.message}`)
  return data as CareRequest
}

export async function supervisorReject(
  client: SupabaseClient,
  requestId: string,
): Promise<CareRequest> {
  const { data, error } = await client.rpc('care_supervisor_reject', {
    p_request_id: requestId,
  })
  if (error) throw new Error(`care.reject_failed: ${error.message}`)
  return data as CareRequest
}

/**
 * Early cancellation. Reverts rates the same way expiry does.
 * Server-side only (uses service role).
 */
export async function cancelActive(
  requestId: string,
  opts: { by: 'therapist' | 'system' | 'supervisor' },
): Promise<CareRequest> {
  const supabase = getAdminClient()
  const { data, error } = await supabase.rpc('care_cancel', {
    p_request_id: requestId,
    p_by: opts.by,
  })
  if (error) throw new Error(`care.cancel_failed: ${error.message}`)
  return data as CareRequest
}

/**
 * Cron-driven expiry. Returns the expired request row.
 * Server-side only.
 */
export async function expireRequest(
  requestId: string,
): Promise<CareRequest> {
  const supabase = getAdminClient()
  const { data, error } = await supabase.rpc('care_expire', {
    p_request_id: requestId,
  })
  if (error) throw new Error(`care.expire_failed: ${error.message}`)
  return data as CareRequest
}

export async function listForTherapist(
  client: SupabaseClient,
  therapistProId: string,
  opts: { status?: CareRequestStatus[] } = {},
): Promise<CareRequest[]> {
  let q = client
    .from('care_requests')
    .select('*')
    .eq('therapistProId', therapistProId)
    .order('createdAt', { ascending: false })
  if (opts.status && opts.status.length > 0) q = q.in('status', opts.status)
  const { data, error } = await q
  if (error) throw new Error(`care.list_therapist_failed: ${error.message}`)
  return (data ?? []) as CareRequest[]
}

export async function listForSupervisor(
  client: SupabaseClient,
  supervisorProId: string,
  opts: { status?: CareRequestStatus[] } = {},
): Promise<CareRequest[]> {
  let q = client
    .from('care_requests')
    .select('*')
    .eq('supervisorProId', supervisorProId)
    .order('createdAt', { ascending: false })
  if (opts.status && opts.status.length > 0) q = q.in('status', opts.status)
  const { data, error } = await q
  if (error) throw new Error(`care.list_supervisor_failed: ${error.message}`)
  return (data ?? []) as CareRequest[]
}

export async function countActiveForTherapist(
  client: SupabaseClient,
  therapistProId: string,
): Promise<number> {
  const { count, error } = await client
    .from('care_requests')
    .select('id', { count: 'exact', head: true })
    .eq('therapistProId', therapistProId)
    .eq('status', 'ACTIVE')
  if (error) return 0
  return count ?? 0
}

export async function countPendingForSupervisor(
  client: SupabaseClient,
  supervisorProId: string,
): Promise<number> {
  const { count, error } = await client
    .from('care_requests')
    .select('id', { count: 'exact', head: true })
    .eq('supervisorProId', supervisorProId)
    .eq('status', 'SUPERVISOR_REVIEW')
  if (error) return 0
  return count ?? 0
}

export async function getCareRequest(
  client: SupabaseClient,
  id: string,
): Promise<CareRequest | null> {
  const { data, error } = await client
    .from('care_requests')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) return null
  return (data as CareRequest) ?? null
}

/**
 * 1-hour signed URL for an uploaded proof document. Uses admin client
 * (per feedback_signed_urls_for_storage.md). Caller must have already
 * established that the user is allowed to view this file.
 */
export async function signedProofUrl(
  path: string,
  expiresInSeconds = 3600,
): Promise<string | null> {
  const supabase = getAdminClient()
  const { data, error } = await supabase.storage
    .from(CARE_HARDSHIP_PROOF_BUCKET)
    .createSignedUrl(path, expiresInSeconds)
  if (error || !data) return null
  return data.signedUrl
}

/**
 * Find ACTIVE requests reaching their end date. Used by the daily cron.
 */
export async function listRequestsToExpire(
  asOf: Date = new Date(),
): Promise<CareRequest[]> {
  const supabase = getAdminClient()
  const cutoff = toDateString(asOf)
  const { data, error } = await supabase
    .from('care_requests')
    .select('*')
    .eq('status', 'ACTIVE')
    .lte('endDate', cutoff)
  if (error) throw new Error(`care.list_expire_failed: ${error.message}`)
  return (data ?? []) as CareRequest[]
}

/**
 * Find ACTIVE requests whose endDate is exactly 7 days out — used to fire
 * "CARE expiring soon" reminders.
 */
export async function listRequestsForReminder(
  asOf: Date = new Date(),
): Promise<CareRequest[]> {
  const supabase = getAdminClient()
  const target = addMonths(asOf, 0)
  target.setDate(target.getDate() + 7)
  const dateStr = toDateString(target)
  const { data, error } = await supabase
    .from('care_requests')
    .select('*')
    .eq('status', 'ACTIVE')
    .eq('endDate', dateStr)
  if (error) throw new Error(`care.list_reminder_failed: ${error.message}`)
  return (data ?? []) as CareRequest[]
}

export const careService = {
  createCareRequest,
  supervisorApprove,
  supervisorReject,
  cancelActive,
  expireRequest,
  listForTherapist,
  listForSupervisor,
  countActiveForTherapist,
  countPendingForSupervisor,
  getCareRequest,
  signedProofUrl,
  listRequestsToExpire,
  listRequestsForReminder,
}
